import { useEffect, useRef } from 'react';

import { useAuthModal } from '../auth-modal-provider';
import {
  closeAuthModal,
  getActiveAuthModalStore,
  notifyAuthLogin,
  type View,
  VIEW_SIGNUP,
} from '../store/auth-modal-store';

/**
 * Transitional adapter that drives the legacy OC auth widget from the framework-agnostic auth-modal
 * store (NGF-6331). Mounted once inside `AuthModalProvider` in the apps that still render the OC widget
 * instead of the native `<Authentication>` modal (RR7 atlas, atlas-astro).
 *
 * This component is the ONLY place — alongside `legacy-auth-modal` and `use-myoc-login-init` — that
 * references `window.ocAuth` / `window.openModalAtView` / `#myoc-login-modal` / `OC:*`. Consumers go
 * through the store instead, so a later ticket can delete the legacy widget by swapping this one file
 * for the native modal. Renders nothing.
 */

// Minimal view of the legacy globals so the new package doesn't depend on the legacy modal's types.
// `openModalAtView` is installed by `legacy-auth-modal` (RR7 atlas only); `ocAuth` is the OC widget
// bundle loaded by `useMyOCLoginInit` (both apps).
type LegacyWindow = Window & {
  closeAllModals?: () => void;
  ocAuth?: { openAtView: (view: string) => void };
  openModalAtView?: (view: string) => void;
};

const PREFILL_RETRY_LIMIT = 4;
const PREFILL_RETRY_DELAY_MS = 500;

// The OC widget renders its email field asynchronously, so we poll briefly for it. The two markets
// mount the widget differently, so the selector must match both: RR7 atlas wraps it in the
// legacy-auth-modal's `#myoc-login-modal`, whereas atlas-astro drives `window.ocAuth` directly and the
// widget mounts straight into its own `.OCwidgetMount` with no such wrapper. (The previous selector
// only matched the RR7 wrapper, so prefill silently no-opped on atlas-astro — NGF-6309.) A plain
// `value` assignment sticks on the widget's email input (verified: it isn't a React-controlled input
// that clobbers the value on re-render), so no synthetic-event dispatch is needed.
const PREFILL_EMAIL_SELECTOR = "div[id='myoc-login-modal'] input[type=email], .OCwidgetMount input[type=email]";

type PrefillController = { cancel: () => void; prefill: (email: string) => void };

// Per-instance prefill poller. The pending retry handle is closure-scoped (not a module global) so a
// second bridge — a Storybook story, an HMR re-mount race, a leaked test mount — keeps its own timer
// and can't cancel another instance's in-flight poll (NGF-6331 review). Mirrors the per-store state
// pattern in betslip-store.ts. `cancel` lets an open/close/unmount drop a poll before it fires against
// a stale (or torn-down) modal; the handle is the browser `window.setTimeout` number (client-only).
const createPrefillController = (): PrefillController => {
  let timeoutId: number | undefined;

  const cancel = (): void => {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  const poll = (email: string, attempt: number): void => {
    timeoutId = undefined;
    const input = document.querySelector<HTMLInputElement>(PREFILL_EMAIL_SELECTOR);
    if (input) {
      input.value = email;
      return;
    }
    if (attempt >= PREFILL_RETRY_LIMIT) return;
    timeoutId = window.setTimeout(() => poll(email, attempt + 1), PREFILL_RETRY_DELAY_MS);
  };

  return { cancel, prefill: (email: string) => poll(email, 0) };
};

const openLegacyWidget = (view: View, prefill: PrefillController, prefillEmail?: string): void => {
  const legacyWindow = window as LegacyWindow;
  const isSignup = view === VIEW_SIGNUP;

  if (typeof legacyWindow.openModalAtView === 'function') {
    // RR7 atlas: route through the legacy modal, which handles OC widget readiness itself.
    legacyWindow.openModalAtView(isSignup ? 'SignupForm' : 'LoginForm');
  } else if (legacyWindow.ocAuth) {
    // atlas-astro: no legacy modal; drive the OC widget directly.
    legacyWindow.ocAuth.openAtView(isSignup ? 'SIGN_UP' : 'LOGIN');
  } else {
    // Widget not ready yet — a pre-ready open is a no-op, matching the prior direct-call behaviour.
    return;
  }

  // Cancel any stale poll from a previous open before starting a fresh one.
  prefill.cancel();
  if (prefillEmail) prefill.prefill(prefillEmail);
};

const closeLegacyWidget = (prefill: PrefillController): void => {
  prefill.cancel();
  const legacyWindow = window as LegacyWindow;
  if (typeof legacyWindow.closeAllModals === 'function') legacyWindow.closeAllModals();
  else legacyWindow.ocAuth?.openAtView('Close');
};

export const LegacyAuthModalBridge = (): null => {
  const { isOpen, prefillEmail, view } = useAuthModal();
  const wasOpenRef = useRef(false);
  const viewRef = useRef(view);
  const prefillEmailRef = useRef(prefillEmail);
  // One prefill poller per mounted bridge, so its retry timer can't be cancelled by another instance.
  const prefillRef = useRef<PrefillController | null>(null);
  if (!prefillRef.current) prefillRef.current = createPrefillController();
  const prefill = prefillRef.current;
  useEffect(() => {
    viewRef.current = view;
    prefillEmailRef.current = prefillEmail;
  });

  // Store → legacy widget: act only on open/close transitions so a view/prefill change while already
  // open never re-opens the widget. Refs ensure the latest view/prefillEmail are read at transition
  // time without making them deps (which would cause spurious no-op re-runs).
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true;
      openLegacyWidget(viewRef.current, prefill, prefillEmailRef.current);
    } else if (!isOpen && wasOpenRef.current) {
      wasOpenRef.current = false;
      closeLegacyWidget(prefill);
    }
  }, [isOpen, prefill]);

  // Legacy widget → store: translate the OC widget's lifecycle events into store writes so consumers
  // never listen to OC:* directly. `getActiveAuthModalStore` is read lazily inside the handlers, by
  // which point the parent provider has published its store.
  useEffect(() => {
    const onLogin = (): void => {
      const store = getActiveAuthModalStore();
      if (store) notifyAuthLogin(store);
    };
    const onClosed = (): void => {
      // Reflect a widget-initiated close in the store without re-issuing closeLegacyWidget (the widget
      // already closed itself), so clear the transition guard before updating state. Skip the write
      // when the store is already closed (e.g. a store-initiated close echoed back as OC:ModalClosed)
      // to avoid a redundant setState/notify.
      wasOpenRef.current = false;
      const store = getActiveAuthModalStore();
      if (store?.getState().isOpen) closeAuthModal(store);
    };
    window.addEventListener('OC:LoggedIn', onLogin);
    window.addEventListener('OC:ModalClosed', onClosed);
    return () => {
      window.removeEventListener('OC:LoggedIn', onLogin);
      window.removeEventListener('OC:ModalClosed', onClosed);
      // Drop any in-flight prefill poll so it can't fire after the bridge unmounts.
      prefill.cancel();
    };
  }, [prefill]);

  return null;
};

LegacyAuthModalBridge.displayName = 'LegacyAuthModalBridge';
